import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import { Sizes, generateAnimations, destroyAnimations } from '../utils';

gsap.registerPlugin( CustomEase );

export class MouseMove {
	constructor( selector, options = {} ) {
		// General
		this.elements = gsap.utils.toArray( selector );
		this.sizes = new Sizes();
		this.options = {
			...options,
			devices: options?.devices || [ 'Desktop', 'Tablet', 'Mobile' ],
			duration: options.duration || 0.5,
		};
		this.isEnable = false;

		// Animations
		this.timelines = [];
		this.textInstances = [];

		this.enterFuncs = [];
		this.leaveFuncs = [];
		this.moveFuncs = [];

		this.xTo = [];
		this.yTo = [];
	}

	init() {
		if (
			! this.elements?.length ||
			this.isEnable ||
			! this.options.devices.includes( this.sizes.device )
		)
			return;
		let ease = 'none';
		if ( this.options.customEase ) {
			ease = CustomEase.create( 'custom', this.options.customEase );
		} else if ( this.options.ease ) {
			ease = this.options.ease;
		}

		this.elements.forEach( ( ele, index ) => {
			// Generate Animations
			const [ theTimelines, theTextInstance ] = generateAnimations(
				ele,
				this.options.animations
			);
			this.timelines[ index ] = theTimelines;
			this.textInstances[ index ] = theTextInstance;

			let timelineX, timelineY;
			if ( this.options.mouseX ) {
				if ( this.options.mouseX.animation ) {
					const animationID =
						this.options.mouseX.animation.theAnimation;
					timelineX = theTimelines.find(
						( item ) => item.animationID === animationID
					)?.timeline;
				}
			}
			if ( this.options.mouseY ) {
				if ( this.options.mouseY.animation ) {
					const animationID =
						this.options.mouseY.animation.theAnimation;
					timelineY = theTimelines.find(
						( item ) => item.animationID === animationID
					)?.timeline;
				}
			}

			if ( timelineX ) {
				this.xTo[ index ] = gsap.quickTo( timelineX, 'progress', {
					duration: this.options.duration,
					ease,
				} );
				gsap.set( timelineX, {
					progress: 0.5,
				} );
			}

			if ( timelineY ) {
				this.yTo[ index ] = gsap.quickTo( timelineY, 'progress', {
					duration: this.options.duration,
					ease,
				} );
				gsap.set( timelineY, {
					progress: 0.5,
				} );
			}

			// Add Events
			this.moveFuncs[ index ] = ( e ) =>
				this.#move( e, ele, this.xTo[ index ], this.yTo[ index ] );
			this.enterFuncs[ index ] = () => this.#enter( ele, index );
			this.leaveFuncs[ index ] = () =>
				this.#leave( ele, index, this.xTo[ index ], this.yTo[ index ] );
			ele.addEventListener( 'mouseenter', this.enterFuncs[ index ] );
			ele.addEventListener( 'mouseleave', this.leaveFuncs[ index ] );
		} );
		this.isEnable = true;
	}

	kill() {
		if ( ! this.isEnable ) return;
		destroyAnimations(
			this.elements,
			this.options.animations,
			this.timelines,
			this.textInstances
		);
		this.elements.forEach( ( ele, index ) => {
			if ( this.enterFuncs[ index ] ) {
				ele.removeEventListener(
					'mouseenter',
					this.enterFuncs[ index ]
				);
			}
			if ( this.leaveFuncs[ index ] ) {
				ele.removeEventListener(
					'mouseleave',
					this.leaveFuncs[ index ]
				);
			}
		} );

		this.timelines = [];
		this.textInstances = [];
		this.enterFuncs = [];
		this.leaveFuncs = [];
		this.moveFuncs = [];
		this.xTo = [];
		this.yTo = [];
		this.isEnable = false;
	}

	resize() {
		this.kill();
		this.init();
	}

	destroy() {
		this.kill();
		this.elements = null;
		this.sizes = null;
		this.options = null;
		this.isEnable = null;
		this.timelines = null;
		this.textInstances = null;
		this.enterFuncs = null;
		this.leaveFuncs = null;
		this.moveFuncs = null;
		this.xTo = null;
		this.yTo = null;
	}

	#enter( ele, index ) {
		ele.addEventListener( 'mousemove', this.moveFuncs[ index ] );
	}

	#leave( ele, index, xTo, yTo ) {
		ele.removeEventListener( 'mousemove', this.moveFuncs[ index ] );
		xTo?.( 0.5 );
		yTo?.( 0.5 );
	}

	#move( e, ele, xTo, yTo ) {
		const bounding = ele.getBoundingClientRect();
		if ( xTo ) {
			const xPercent = ( e.clientX - bounding.left ) / ele.offsetWidth;
			xTo( xPercent );
		}
		if ( yTo ) {
			const yPercent = ( e.clientY - bounding.top ) / ele.offsetHeight;
			yTo( yPercent );
		}
	}
}
